"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEvaluatedCofConfig = exports.registerPropertyValueProvider = void 0;
const config_result_1 = require("../generated/cdp/cof/config_result");
const util_1 = require("./util");
/**
 * Map for developer-provided callbacks on how to retrieve values for client targeting properties.
 */
const targetingPropertyValueProviders = new Map();
/**
 * Registers providers for targeting property values. These providers will be used when evaluating config rules
 * using client-side targeting expressions.
 */
function registerPropertyValueProvider(targetingProperty, provider) {
    targetingPropertyValueProviders.set(targetingProperty, provider);
}
exports.registerPropertyValueProvider = registerPropertyValueProvider;
/**
 * Takes a list of config rules and returns the highest priority one that matches the configId and that has a
 * satisfied targeting expression. If a provided config rule does not have a targeting expression, that means that
 * the targeting expression was evaluated server-side and already deemed to be satisfied. If no config rules
 * have targeting expressions that are satisfied or no config rule with the config name was returned, undefined is
 * returned.
 * @param configResults - List of ConfigResult-like objects (only configId and targetingExpression must be present)
 * @param configName
 */
function getEvaluatedCofConfig(configResults, configName) {
    const configRulesForConfigId = configResults.filter((configRule) => configRule.configId === configName);
    const noExpressionConfigRules = configRulesForConfigId.filter((configRule) => 
    // Depending on the language for the generated proto code, targetingExpression may either be undefined or the
    // default proto message.
    !configRule.targetingExpression ||
        (configRule.targetingExpression &&
            util_1.isDefaultTargetingExpression(configRule.targetingExpression)));
    const clientTargetingExpressionConfigRules = configRulesForConfigId.filter((configRule) => configRule.targetingExpression &&
        !util_1.isDefaultTargetingExpression(configRule.targetingExpression));
    const satisfiedClientConfigs = clientTargetingExpressionConfigRules.filter((configRule) => isTargetingExpressionSatisfied(configRule.targetingExpression));
    const sortedConfigs = [
        ...satisfiedClientConfigs,
        ...noExpressionConfigRules,
    ].sort((a, b) => {
        return b.priority - a.priority;
    });
    return sortedConfigs.length ? sortedConfigs[0] : undefined;
}
exports.getEvaluatedCofConfig = getEvaluatedCofConfig;
function isTargetingExpressionSatisfied(targetingExpression) {
    if (targetingExpression.children.length > 0) {
        if (targetingExpression.operator === config_result_1.ClientTargetingExpression_Operator.AND) {
            return targetingExpression.children.every((childExpression) => isTargetingExpressionSatisfied(childExpression));
        }
        else if (targetingExpression.operator === config_result_1.ClientTargetingExpression_Operator.OR) {
            return targetingExpression.children.some((childExpression) => isTargetingExpressionSatisfied(childExpression));
        }
        else {
            return false;
        }
    }
    return evaluateLeafNode(targetingExpression);
}
function evaluateLeafNode(leaf) {
    var _a, _b, _c, _d, _e, _f;
    // If it's not actually a leaf node for whatever reason, consider the evaluation result to be false.
    if (!leaf.property ||
        !leaf.value ||
        !leaf.predicateOperator ||
        leaf.children.length > 0) {
        return false;
    }
    const propertyValueProvider = targetingPropertyValueProviders.get(leaf.property);
    if (!propertyValueProvider) {
        return false;
    }
    const propertyValue = propertyValueProvider();
    const comparisonValue = (_f = (_e = (_d = (_c = (_b = (_a = leaf.value.boolValue) !== null && _a !== void 0 ? _a : leaf.value.doubleValue) !== null && _b !== void 0 ? _b : leaf.value.floatValue) !== null && _c !== void 0 ? _c : leaf.value.intPairValue) !== null && _d !== void 0 ? _d : leaf.value.intValue) !== null && _e !== void 0 ? _e : leaf.value.longValue) !== null && _f !== void 0 ? _f : leaf.value.stringValue;
    // >= or <= operators can only operate on numbers (or proto enums)
    if (leaf.predicateOperator ===
        config_result_1.ClientTargetingExpression_PredicateOperator.GREATER_THAN_OR_EQUAL_TO ||
        leaf.predicateOperator ===
            config_result_1.ClientTargetingExpression_PredicateOperator.LESS_THAN_OR_EQUAL_TO) {
        if (typeof comparisonValue !== "number") {
            return false;
        }
        if (typeof propertyValue !== "number") {
            return false;
        }
        if (leaf.predicateOperator ===
            config_result_1.ClientTargetingExpression_PredicateOperator.LESS_THAN_OR_EQUAL_TO) {
            return propertyValue <= comparisonValue;
        }
        else {
            return propertyValue >= comparisonValue;
        }
    }
    if (leaf.predicateOperator ===
        config_result_1.ClientTargetingExpression_PredicateOperator.NOT_EQUAL) {
        return propertyValue !== comparisonValue;
    }
    if (leaf.predicateOperator === config_result_1.ClientTargetingExpression_PredicateOperator.EQUAL) {
        return propertyValue === comparisonValue;
    }
    return false;
}
//# sourceMappingURL=evaluator.js.map